import React, { useState, useCallback, useEffect } from 'react';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { RefreshCw, AlertCircle, Plus, Search, X, ChevronLeft, ChevronRight, Save } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import config from '@/config';

// Node Selection Modal Component
function NodeSelectionModal({ isOpen, onClose, nodeDefinitions, onSelectNode }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredNodes, setFilteredNodes] = useState(nodeDefinitions);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredNodes(nodeDefinitions);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = nodeDefinitions.filter(node => {
      const nameMatch = node.name?.toLowerCase().includes(term);
      const descMatch = node.description?.toLowerCase().includes(term);
      const inputKindMatch = node.input_kind?.toLowerCase().includes(term);
      const outputKindMatch = node.output_kind?.toLowerCase().includes(term);
      
      const inputSchemaMatch = node.input_schema?.some(input => 
        input.name?.toLowerCase().includes(term) || 
        input.type?.toLowerCase().includes(term)
      );
      
      const outputSchemaMatch = node.output_schema?.some(output => 
        output.name?.toLowerCase().includes(term) || 
        output.type?.toLowerCase().includes(term)
      );

      return nameMatch || descMatch || inputKindMatch || outputKindMatch || inputSchemaMatch || outputSchemaMatch;
    });

    setFilteredNodes(filtered);
  }, [searchTerm, nodeDefinitions]);

  const handleSelectNode = (node) => {
    onSelectNode(node);
    onClose();
    setSearchTerm('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Plus className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-bold">Select Workflow Node</h2>
              <p className="text-blue-100 text-sm mt-1">
                Choose a node type to add to your workflow
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, description, input/output types..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
            />
          </div>
          <div className="mt-2 text-sm text-gray-600">
            Showing {filteredNodes.length} of {nodeDefinitions.length} nodes
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {filteredNodes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <Search className="w-16 h-16 mb-3" />
              <p className="text-lg font-medium">No nodes found</p>
              <p className="text-sm">Try adjusting your search term</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {filteredNodes.map((node) => (
                <div
                  key={node.id}
                  onClick={() => handleSelectNode(node)}
                  className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:border-blue-500 hover:shadow-lg transition-all cursor-pointer"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-900 text-base mb-1">
                        {node.name}
                      </h3>
                      
                      {node.description && (
                        <p className="text-sm text-gray-600 mb-3">
                          {node.description}
                        </p>
                      )}

                      <div className="flex flex-wrap gap-2 mb-3">
                        {node.input_kind && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                            <span className="font-semibold">Input:</span> {node.input_kind}
                          </span>
                        )}
                        {node.output_kind && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                            <span className="font-semibold">Output:</span> {node.output_kind}
                          </span>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectNode(node);
                      }}
                      className="flex-shrink-0 p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Settings Node Component
const SettingsNode = ({ data }) => {
  const { nodeDef, instanceId, selectedSetting, settingOutputs, onSettingSelect } = data;
  const { authenticatedFetch } = useAuth();
  const [availableSettings, setAvailableSettings] = useState([]);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [settingsError, setSettingsError] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      setLoadingSettings(true);
      setSettingsError(null);
      
      try {
        const response = await authenticatedFetch(
          `${config.BACKEND_API_ADDRESS}/workflows2/settings/list`
        );
        
        if (!response.ok) {
          throw new Error(`Failed to fetch settings: ${response.status}`);
        }
        
        const settings = await response.json();
        setAvailableSettings(settings);
      } catch (err) {
        console.error('Error fetching settings:', err);
        setSettingsError(err.message);
      } finally {
        setLoadingSettings(false);
      }
    };

    fetchSettings();
  }, [authenticatedFetch]);

  const handleSettingChange = async (settingId) => {
    if (!settingId) return;

    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/workflows2/settings/${settingId}`
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch setting details: ${response.status}`);
      }
      
      const settingData = await response.json();
      onSettingSelect(instanceId, settingId, settingData.outputs);
    } catch (err) {
      console.error('Error fetching setting details:', err);
    }
  };

  return (
    <div className="shadow-lg rounded-lg bg-white border-2 border-purple-400 min-w-[280px] relative">
      <div className="px-4 py-3 bg-purple-100 border-b border-purple-300 rounded-t-lg">
        <div className="font-bold text-sm text-purple-900">{nodeDef.name}</div>
        <div className="text-xs text-purple-600 truncate">ID: {instanceId}</div>
      </div>
      
      <div className="px-4 py-3">
        <div className="text-xs font-semibold text-purple-600 mb-2">Select Setting</div>
        
        {loadingSettings ? (
          <div className="flex items-center gap-2 text-gray-500 text-xs">
            <RefreshCw className="w-3 h-3 animate-spin" />
            <span>Loading settings...</span>
          </div>
        ) : settingsError ? (
          <div className="flex items-center gap-2 text-red-500 text-xs">
            <AlertCircle className="w-3 h-3" />
            <span>{settingsError}</span>
          </div>
        ) : (
          <select
            value={selectedSetting || ''}
            onChange={(e) => handleSettingChange(e.target.value)}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
            onClick={(e) => e.stopPropagation()}
          >
            <option value="">-- Select a setting --</option>
            {availableSettings.map((setting) => (
              <option key={setting.id} value={setting.id}>
                {setting.name || setting.id}
              </option>
            ))}
          </select>
        )}
      </div>
      
      {settingOutputs && settingOutputs.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-200">
          <div className="text-xs font-semibold text-green-600 mb-2">Outputs</div>
          {settingOutputs.map((output, idx) => (
            <div key={output.id || idx} className="relative mb-2 pr-3">
              <Handle
                type="source"
                position={Position.Right}
                id={`${instanceId}-output-${output.id}`}
                style={{ right: '-8px', background: '#10b981', width: '10px', height: '10px' }}
              />
              <div className="text-xs text-gray-700 text-right bg-green-50 rounded p-2">
                <div className="font-medium">{output.id}</div>
                <div className="text-gray-500">Kind: {output.kind}</div>
                <div className="text-green-700 font-mono mt-1">
                  Value: {JSON.stringify(output.value)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Custom Node Component
const CustomNode = ({ data }) => {
  const { nodeDef, instanceId, inputValues, onInputChange, presentInputValues = {}, presentOutputValues = {} } = data;
  
  return (
    <div className="shadow-lg rounded-lg bg-white border-2 border-gray-300 min-w-[280px] relative">
      <div className="px-4 py-3 bg-gray-100 border-b border-gray-300 rounded-t-lg">
        <div className="font-bold text-sm text-gray-800">{nodeDef.name}</div>
        <div className="text-xs text-gray-500 truncate">ID: {instanceId}</div>
      </div>
      
      <div className="grid grid-cols-2 gap-4 px-4 py-3">
        <div>
          <div className="text-xs font-semibold text-blue-600 mb-2">Inputs</div>
          {nodeDef.input_schema && nodeDef.input_schema.length > 0 ? (
            nodeDef.input_schema.map((input, idx) => {
              const presentValue = presentInputValues[input.id];
              const hasConnectedValue = presentValue !== undefined && presentValue !== null;
              
              return (
                <div key={input.id || idx} className="relative mb-3 pl-3">
                  <Handle
                    type="target"
                    position={Position.Left}
                    id={`${instanceId}-input-${input.id}`}
                    style={{ left: '-8px', background: '#3b82f6', width: '10px', height: '10px' }}
                  />
                  <div className="text-xs text-gray-700">
                    <div className="text-[10px] font-semibold text-blue-700 uppercase mb-0.5">
                      {input.name}
                    </div>
                    
                    {hasConnectedValue ? (
                      <div className="font-mono text-xs font-semibold text-blue-900 bg-blue-100 px-2 py-1 rounded mb-1 break-all border border-blue-300">
                        {typeof presentValue === 'string' 
                          ? `"${presentValue}"` 
                          : typeof presentValue === 'object'
                          ? JSON.stringify(presentValue)
                          : String(presentValue)}
                      </div>
                    ) : input.allow_manual_input ? (
                      <div className="mt-1">
                        {input.type === 'number' ? (
                          <input
                            type="number"
                            placeholder={input.default !== undefined ? String(input.default) : 'Enter value'}
                            value={inputValues?.[input.id] || ''}
                            onChange={(e) => onInputChange(input.id, e.target.value)}
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : input.type === 'boolean' ? (
                          <label className="flex items-center gap-1 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={inputValues?.[input.id] || false}
                              onChange={(e) => onInputChange(input.id, e.target.checked)}
                              className="w-3 h-3"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span className="text-xs text-gray-600">Enable</span>
                          </label>
                        ) : (
                          <input
                            type="text"
                            placeholder={input.default || 'Enter value'}
                            value={inputValues?.[input.id] || ''}
                            onChange={(e) => onInputChange(input.id, e.target.value)}
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                      </div>
                    ) : null}
                    
                    {!hasConnectedValue && (
                      <div className="text-gray-400 text-[10px]">
                        {input.type}
                        {input.required && <span className="text-red-500 ml-1">*</span>}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-xs text-gray-400 italic">No inputs</div>
          )}
        </div>
        
        <div>
          <div className="text-xs font-semibold text-green-600 mb-2">Outputs</div>
          {nodeDef.output_schema && nodeDef.output_schema.length > 0 ? (
            nodeDef.output_schema.map((output, idx) => {
              const presentValue = presentOutputValues[output.id];
              const hasValue = presentValue !== undefined && presentValue !== null;
              
              return (
                <div key={output.id || idx} className="relative mb-2 pr-3">
                  <Handle
                    type="source"
                    position={Position.Right}
                    id={`${instanceId}-output-${output.id}`}
                    style={{ right: '-8px', background: '#10b981', width: '10px', height: '10px' }}
                  />
                  <div className="text-xs text-gray-700 text-right">
                    <div className="text-[10px] font-semibold text-green-700 uppercase mb-0.5">
                      {output.name}
                    </div>
                    {hasValue && (
                      <div className="font-mono text-xs font-semibold text-green-900 bg-green-100 px-2 py-1 rounded mb-1 break-all border border-green-300">
                        {typeof presentValue === 'string' 
                          ? `"${presentValue}"` 
                          : typeof presentValue === 'object'
                          ? JSON.stringify(presentValue)
                          : String(presentValue)}
                      </div>
                    )}
                    <div className="text-gray-400 text-[10px]">{output.type}</div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-xs text-gray-400 italic text-right">No outputs</div>
          )}
        </div>
      </div>
    </div>
  );
};

const nodeTypes = {
  custom: CustomNode,
  settings: SettingsNode,
};

export default function TemplateEditor({ initialTemplateId, initialTemplateIdentifier }) {
  const { authenticatedFetch } = useAuth();
  
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [nodeInstances, setNodeInstances] = useState({});
  const [nodeDefinitions, setNodeDefinitions] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dependencyTreeOpen, setDependencyTreeOpen] = useState(false);
  
  // Template metadata
  const [templateId, setTemplateId] = useState(initialTemplateId || '');
  const [templateIdentifier, setTemplateIdentifier] = useState(initialTemplateIdentifier || '');
  const [searchMode, setSearchMode] = useState('id');
  const [templateTitle, setTemplateTitle] = useState('');
  const [loadedTemplateIdentifier, setLoadedTemplateIdentifier] = useState('');
  const [templateLoaded, setTemplateLoaded] = useState(false);
  const [originalTemplateId, setOriginalTemplateId] = useState(null);
  
  // Auto-load template if ID or identifier is provided as prop
  useEffect(() => {
    const autoLoad = async () => {
      if ((initialTemplateId || initialTemplateIdentifier) && nodeDefinitions.length > 0 && !templateLoaded) {
        await loadTemplate();
      }
    };
    autoLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTemplateId, initialTemplateIdentifier, nodeDefinitions.length, templateLoaded]);
  
  // Fetch workflow items from backend
  useEffect(() => {
    const fetchWorkflowItems = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await authenticatedFetch(
          `${config.BACKEND_API_ADDRESS}/workflows2/workflow-items/nodes`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setNodeDefinitions(data);
      } catch (err) {
        console.error('Error fetching workflow items:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkflowItems();
  }, [authenticatedFetch]);

  const loadTemplate = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const endpoint = (initialTemplateId || templateId)
        ? `${config.BACKEND_API_ADDRESS}/workflows2/template/${initialTemplateId || templateId}`
        : `${config.BACKEND_API_ADDRESS}/workflows2/template/by-identifier/${initialTemplateIdentifier || templateIdentifier}`;
      
      const response = await authenticatedFetch(endpoint);
      
      if (!response.ok) {
        throw new Error(`Failed to load template: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Set template metadata
      setOriginalTemplateId(data.id);
      setTemplateTitle(data.name);
      setLoadedTemplateIdentifier(data.identifier);
      
      const instances = {};
      data.nodes.forEach(node => {
        instances[node.id] = {
          nodeDefId: node.workflow_item_id,
          nodeTypeId: node.workflow_item_id,
          outputs: node.output_data || {},
          inputConnections: {},
          manualInputValues: node.selected_data?.manual_inputs || {},  // ← Change here!
          selectedSetting: node.selected_data?.selected_setting,
          settingOutputs: node.selected_data?.setting_outputs || []
        };
      });
      
      // Build input connections from dependencies
      data.dependencies.forEach(dep => {
        const connectedFields = dep.connected_fields || {};
        if (!instances[dep.node_id].inputConnections) {
          instances[dep.node_id].inputConnections = {};
        }
        instances[dep.node_id].inputConnections[connectedFields.target_input_id] = {
          source_instance_id: dep.depends_on_node_id,
          source_output_id: connectedFields.source_output_id
        };
      });
      
      setNodeInstances(instances);
      
      // Build React Flow nodes
  const flowNodes = data.nodes.map(node => {
    const workflowItem = nodeDefinitions.find(item => item.id === node.workflow_item_id);
    const position = data.structure?.positions?.[node.id] || { x: Math.random() * 400, y: Math.random() * 300 };
    
    const isSettingsNode = workflowItem?.identifier === 'glasses_get_settings';
    
    const inputChangeCallback = (inputId, value) => handleInputChange(node.id, inputId, value);
    const settingSelectCallback = (instId, settingId, settingOutputs) => 
      handleSettingSelect(instId, settingId, settingOutputs);
    
    return {
      id: node.id,
      type: isSettingsNode ? 'settings' : 'custom',
      position,
      data: {
        nodeDef: workflowItem || {
          name: node.workflow_item_name,
          input_schema: [],
          output_schema: []
        },
        instanceId: node.id,
        inputValues: instances[node.id].manualInputValues,
        onInputChange: inputChangeCallback,
        presentInputValues: {},
        presentOutputValues: node.output_data || {}, // ← ADD THIS: Load output_data here too!
        ...(isSettingsNode && {
          onSettingSelect: settingSelectCallback,
          selectedSetting: instances[node.id].selectedSetting,
          settingOutputs: instances[node.id].settingOutputs
        })
      }
    };
  });
      
      // Build React Flow edges
      const flowEdges = data.dependencies.map(dep => {
        const connectedFields = dep.connected_fields || {};
        return {
          id: dep.id,
          source: dep.depends_on_node_id,
          target: dep.node_id,
          sourceHandle: `${dep.depends_on_node_id}-output-${connectedFields.source_output_id}`,
          targetHandle: `${dep.node_id}-input-${connectedFields.target_input_id}`,
          animated: true
        };
      });
      
      setNodes(flowNodes);
      setEdges(flowEdges);
      setTemplateLoaded(true);
      
      // Trigger value propagation
      setTimeout(() => propagateValues(), 100);
      
    } catch (err) {
      console.error('Error loading template:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const onConnect = useCallback(
    (params) => {
      if (params.source === params.target) {
        alert('Cannot connect a node to itself!');
        return;
      }

      const sourceNode = nodes.find(n => n.id === params.source);
      const targetNode = nodes.find(n => n.id === params.target);
      
      if (sourceNode && targetNode) {
        const sourceOutputId = params.sourceHandle?.replace(`${params.source}-output-`, '');
        const targetInputId = params.targetHandle?.replace(`${params.target}-input-`, '');
        
        let outputSchema, inputSchema;
        
        if (sourceNode.type === 'settings' && sourceNode.data.settingOutputs) {
          outputSchema = sourceNode.data.settingOutputs.find(o => o.id === sourceOutputId);
        } else {
          outputSchema = sourceNode.data.nodeDef.output_schema?.find(o => o.id === sourceOutputId);
        }
        
        inputSchema = targetNode.data.nodeDef.input_schema?.find(i => i.id === targetInputId);
        
        if (outputSchema && inputSchema) {
          const outputType = outputSchema.type || outputSchema.kind;
          const inputType = inputSchema.type;
          
          if (outputType !== inputType) {
            alert(`Cannot connect: Output type "${outputType}" does not match input type "${inputType}"`);
            return;
          }
        }
      }
      
      setEdges((eds) => addEdge({ ...params, animated: true }, eds));
    },
    [setEdges, nodes]
  );

  const handleInputChange = useCallback((instanceId, inputId, value) => {
    setNodeInstances((prev) => ({
      ...prev,
      [instanceId]: {
        ...prev[instanceId],
        manualInputValues: {
          ...prev[instanceId]?.manualInputValues || {},
          [inputId]: value
        }
      }
    }));

    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === instanceId) {
          return {
            ...node,
            data: {
              ...node.data,
              inputValues: {
                ...node.data.inputValues,
                [inputId]: value
              },
              onInputChange: node.data.onInputChange
            }
          };
        }
        return node;
      })
    );
  }, [setNodes, setNodeInstances]);

  const handleSettingSelect = useCallback((instanceId, settingId, settingOutputs) => {
    setNodeInstances((prev) => ({
      ...prev,
      [instanceId]: {
        ...prev[instanceId],
        selectedSetting: settingId,
        settingOutputs: settingOutputs,
        outputs: settingOutputs.reduce((acc, output) => {
          acc[output.id] = output.value;
          return acc;
        }, {})
      }
    }));

    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === instanceId) {
          return {
            ...node,
            data: {
              ...node.data,
              selectedSetting: settingId,
              settingOutputs: settingOutputs
            }
          };
        }
        return node;
      })
    );
    
    propagateValues();
  }, [setNodes, setNodeInstances]);

  const propagateValues = useCallback(() => {
    const inputConnections = {};
    
    edges.forEach(edge => {
      const sourceOutputId = edge.sourceHandle?.replace(`${edge.source}-output-`, '');
      const targetInputId = edge.targetHandle?.replace(`${edge.target}-input-`, '');
      
      if (!inputConnections[edge.target]) {
        inputConnections[edge.target] = {};
      }
      inputConnections[edge.target][targetInputId] = {
        source_instance_id: edge.source,
        source_output_id: sourceOutputId
      };
    });

  setNodes((nds) =>
    nds.map((node) => {
      const connections = inputConnections[node.id] || {};
      const presentInputValues = {};
      
      Object.entries(connections).forEach(([targetInputId, connection]) => {
        const sourceNode = nds.find(n => n.id === connection.source_instance_id);
        if (sourceNode) {
          let outputValue;
          
          if (sourceNode.type === 'settings' && sourceNode.data.settingOutputs) {
            const output = sourceNode.data.settingOutputs.find(o => o.id === connection.source_output_id);
            outputValue = output?.value;
          } else if (sourceNode.data.presentOutputValues) {
            outputValue = sourceNode.data.presentOutputValues[connection.source_output_id];
          }
          
          if (outputValue !== undefined) {
            presentInputValues[targetInputId] = outputValue;
          }
        }
      });
      
      return {
        ...node,
        data: {
          ...node.data,
          presentInputValues,
          // KEEP the existing presentOutputValues - don't overwrite them!
          presentOutputValues: node.data.presentOutputValues
        }
      };
    })
  );
}, [edges, setNodes]);

  useEffect(() => {
    if (edges.length > 0 && templateLoaded) {
      propagateValues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edges.length, templateLoaded]);

  const addNodeInstance = useCallback((nodeDef) => {
    if (!nodeDef) return;
    
    const instanceId = `instance_${Date.now()}`;
    const isSettingsNode = nodeDef.identifier === 'glasses_get_settings';
    
    const inputChangeCallback = (inputId, value) => handleInputChange(instanceId, inputId, value);
    const settingSelectCallback = (instId, settingId, settingOutputs) => 
      handleSettingSelect(instId, settingId, settingOutputs);
    
    const newNode = {
      id: instanceId,
      type: isSettingsNode ? 'settings' : 'custom',
      position: { x: Math.random() * 400, y: Math.random() * 300 },
      data: { 
        nodeDef, 
        instanceId,
        outputs: {},
        inputValues: {},
        presentInputValues: {},
        presentOutputValues: {},
        onInputChange: inputChangeCallback,
        ...(isSettingsNode && {
          onSettingSelect: settingSelectCallback,
          selectedSetting: null,
          settingOutputs: []
        })
      },
    };
    
    setNodes((nds) => [...nds, newNode]);
    setNodeInstances((prev) => ({
      ...prev,
      [instanceId]: {
        nodeDefId: nodeDef.id,
        nodeTypeId: nodeDef.id,
        outputs: {},
        inputConnections: {},
        manualInputValues: {},
        ...(isSettingsNode && {
          selectedSetting: null,
          settingOutputs: []
        })
      }
    }));
  }, [handleInputChange, handleSettingSelect, setNodes, setNodeInstances]);

const handleUpdateTemplate = async () => {
  if (!originalTemplateId) {
    alert('No template loaded to update');
    return;
  }

  // Only include nodes that still exist in the React Flow canvas
  const activeNodeIds = new Set(nodes.map(n => n.id));
  
  const nodesList = Object.entries(nodeInstances)
    .filter(([instanceId]) => activeNodeIds.has(instanceId))  // ← ADD THIS LINE
    .map(([instanceId, data]) => {
      const node = nodes.find(n => n.id === instanceId);
      const nodeConfig = {
        instance_id: instanceId,
        workflow_item_id: data.nodeTypeId,
        name: node?.data.nodeDef.name,
        input_connections: data.inputConnections || {},
        manual_input_values: data.manualInputValues || {}
      };
      
      if (node?.type === 'settings') {
        nodeConfig.selected_setting = data.selectedSetting;
        nodeConfig.setting_outputs = data.settingOutputs;
      }
      
      return nodeConfig;
    });

    const positionsDict = {};
    nodes.forEach(node => {
      positionsDict[node.id] = {
        x: node.position.x,
        y: node.position.y
      };
    });

    const edgesList = edges.map(edge => ({
      source_instance_id: edge.source,
      source_output_id: edge.sourceHandle?.replace(`${edge.source}-output-`, ''),
      target_instance_id: edge.target,
      target_input_id: edge.targetHandle?.replace(`${edge.target}-input-`, '')
    }));

    try {
      setLoading(true);
      
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/workflows2/template/update/${originalTemplateId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            title: templateTitle,
            identifier: loadedTemplateIdentifier,
            nodes: nodesList,
            positions: positionsDict,
            edges: edgesList
          })
        }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to update template: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('Template updated successfully:', result);
      
      alert(`Template "${templateTitle}" updated successfully!`);
      
    } catch (err) {
      console.error('Error updating template:', err);
      alert(`Error updating template: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getDependencyTree = () => {
    const tree = [];
    Object.entries(nodeInstances).forEach(([instanceId, data]) => {
      const node = nodes.find(n => n.id === instanceId);
      if (node) {
        tree.push({
          id: instanceId,
          name: node.data.nodeDef.name,
          nodeTypeId: data.nodeTypeId,
          dependsOn: data.dependsOn || [],
          inputConnections: data.inputConnections || {},
          manualInputValues: data.manualInputValues || {},
          outputs: data.outputs || {},
          ...(node.type === 'settings' && {
            selectedSetting: data.selectedSetting,
            settingOutputs: data.settingOutputs
          })
        });
      }
    });
    return tree;
  };

  return (
    <div className="w-full h-screen flex flex-col bg-gray-50">
      {/* Toolbar */}
      <div className="bg-white shadow-md p-4 flex items-center gap-4 z-10 flex-wrap">
        {!templateLoaded && !initialTemplateId && !initialTemplateIdentifier ? (
          <>
            <h1 className="text-xl font-bold text-gray-800">Load Template</h1>
            
            <div className="flex items-center gap-2 border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setSearchMode('id')}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  searchMode === 'id' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                By ID
              </button>
              <button
                onClick={() => setSearchMode('identifier')}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  searchMode === 'identifier' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                By Identifier
              </button>
            </div>
            
            {searchMode === 'id' ? (
              <input
                type="text"
                placeholder="Enter template UUID"
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-80"
              />
            ) : (
              <input
                type="text"
                placeholder="Enter template identifier"
                value={templateIdentifier}
                onChange={(e) => setTemplateIdentifier(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-96"
              />
            )}
            
            <button
              onClick={loadTemplate}
              disabled={loading || (searchMode === 'id' ? !templateId : !templateIdentifier) || nodeDefinitions.length === 0}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4" />
                  Load Template
                </>
              )}
            </button>
          </>
        ) : !templateLoaded ? (
          <div className="flex items-center gap-2 text-gray-600">
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span className="text-sm">Loading template...</span>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-4 flex-1">
              <h1 className="text-xl font-bold text-gray-800">Edit Template</h1>
              
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Title:</label>
                <input
                  type="text"
                  placeholder="Enter template title"
                  value={templateTitle}
                  onChange={(e) => setTemplateTitle(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Identifier:</label>
                <div className="px-3 py-1.5 bg-gray-100 text-sm text-gray-700 font-mono border border-gray-300 rounded-lg">
                  {loadedTemplateIdentifier}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <button
                onClick={() => setModalOpen(true)}
                disabled={nodeDefinitions.length === 0}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Node
              </button>
              
              <button
                onClick={handleUpdateTemplate}
                disabled={!templateTitle || loading}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Update Template
                  </>
                )}
              </button>
            </div>
          </>
        )}
        
        {error && (
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">{error}</span>
          </div>
        )}
      </div>

      <div className="flex-1 flex relative">
        <div className="flex-1">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{
              padding: 0.2,
              minZoom: 0.5,
              maxZoom: 1.5
            }}
            minZoom={0.1}
            maxZoom={2}
            defaultZoom={1}
          >
            <Controls />
            <MiniMap />
            <Background variant="dots" gap={12} size={1} />
          </ReactFlow>
        </div>

        {!dependencyTreeOpen && templateLoaded && (
          <button
            onClick={() => setDependencyTreeOpen(true)}
            className="absolute top-4 right-4 p-2 bg-white border-2 border-gray-300 rounded-lg shadow-lg hover:bg-gray-50 transition-colors z-10"
            title="Open Dependency Tree"
          >
            <ChevronLeft className="w-5 h-5 text-gray-700" />
          </button>
        )}

        {dependencyTreeOpen && templateLoaded && (
          <div className="w-80 bg-white border-l border-gray-200 flex flex-col shadow-lg">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
              <h2 className="text-lg font-bold text-gray-800">Dependency Tree</h2>
              <button
                onClick={() => setDependencyTreeOpen(false)}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
                title="Close panel"
              >
                <ChevronRight className="w-5 h-5 text-gray-700" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              {getDependencyTree().length === 0 ? (
                <p className="text-sm text-gray-500">No nodes added yet</p>
              ) : (
                <div className="space-y-4">
                  {getDependencyTree().map((item) => (
                    <div key={item.id} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                      <div className="font-semibold text-sm text-gray-800">{item.name}</div>
                      <div className="text-xs text-gray-500 mb-2 break-all">{item.id}</div>
                      
                      {item.selectedSetting && (
                        <div className="mb-2">
                          <div className="text-xs font-semibold text-purple-600">Selected Setting:</div>
                          <div className="text-xs text-gray-700">{item.selectedSetting}</div>
                        </div>
                      )}
                      
                      {Object.keys(item.inputConnections).length > 0 && (
                        <div className="mb-2">
                          <div className="text-xs font-semibold text-blue-600">Input Connections:</div>
                          <div className="text-xs text-gray-700 font-mono bg-white p-2 rounded mt-1">
                            {JSON.stringify(item.inputConnections, null, 2)}
                          </div>
                        </div>
                      )}
                      
                      {Object.keys(item.manualInputValues || {}).length > 0 && (
                        <div className="mb-2">
                          <div className="text-xs font-semibold text-purple-600">Manual Input Values:</div>
                          <div className="text-xs text-gray-700 font-mono bg-purple-50 p-2 rounded mt-1">
                            {JSON.stringify(item.manualInputValues, null, 2)}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <NodeSelectionModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        nodeDefinitions={nodeDefinitions}
        onSelectNode={addNodeInstance}
      />
    </div>
  );
}